import Experience, {
  useExperienceStore,
  useSectionStore,
} from "@folio-2026/experience"
import { useAppLenis } from "@folio-2026/shared"
import Lenis from "lenis"
import { PropsWithChildren, useCallback, useEffect, useRef } from "react"
import { useLocation } from "react-router"

import useDebug from "hooks/useDebug"

import { SectionDOMElement } from "components/App"

import { handleScroll } from "./handlers"

export default function Scroller({
  children,
  sections,
}: PropsWithChildren<{ sections: SectionDOMElement[] }>) {
  const location = useLocation()
  const { width, height } = useExperienceStore()
  const { currentSection, setCurrentSection } = useSectionStore()
  const debug = useDebug()

  const experience = useRef(Experience.getInstance())

  const setSnaps = useCallback(() => {
    const newSnaps = sections
      .map((section) => {
        const el = document.querySelector(
          `[data-name="${section.name}"]`,
        ) as HTMLDivElement | null
        if (!el) {
          throw new Error(`Section ${section.name} not found`)
        }
        return {
          snap: el.offsetTop,
          name: section.name,
          height: el.clientHeight,
        }
      })
      .sort((a, b) => a.snap - b.snap)

    //
    //
    experience.current.scrollInfo.snaps = newSnaps
  }, [sections])

  useEffect(() => {
    if (width === 0 || height === 0) {
      return
    }
    setSnaps()
  }, [width, height, setSnaps])

  const onLenisUpdate = useCallback(
    (lenis: Lenis) => {
      const snaps = experience.current.scrollInfo.snaps

      handleScroll(lenis, snaps || [], location.hash === "#debug")

      //
      // Update current section in the store
      const progress = experience.current.scrollInfo.progress
      const scrollCurrentSection =
        experience.current.scrollInfo.currentSection?.name
      if (
        typeof progress !== "undefined" ||
        scrollCurrentSection === currentSection
      ) {
        return
      }

      setCurrentSection(
        experience.current.scrollInfo.currentSection?.name ?? "",
      )
    },
    [location.hash, currentSection, setCurrentSection],
  )

  useAppLenis(experience.current, onLenisUpdate)

  return (
    <>
      {debug.active && (
        <>
          <div className="h-[1px] w-[50px] bg-red-500 fixed z-50 top-[30%]" />
          <div className="h-[1px] w-[50px] bg-red-500 fixed z-50 top-auto bottom-[30%]" />
        </>
      )}

      <main className={`relative z-1 pointer-events-none`}>{children}</main>
    </>
  )
}
